import { motion } from "framer-motion";

/** Main landing hero — full-bleed Spline background (robot follow cursor). */
export const HERO_SPLINE_URL =
  "https://my.spline.design/robotfollowcursorforlandingpagemc-QIOkQQAuKFIv98ETYfLVVzQ5/";

/** Bump when Spline export changes — remounts iframe (cache bust). */
export const HERO_SPLINE_EMBED_KEY = "robot-follow-cursor-hero-v5";

/** Card Spline — company /about hero (3D promo cards). Do NOT reuse HERO_SPLINE_URL. */
export const SPLINE_URL =
  "https://my.spline.design/projectpromolookatmouse-03V305jwatX2ptHNWGraixpB/";

export const SPLINE_EMBED_KEY = "projectpromolookatmouse-v2";

/** @deprecated use HERO_SPLINE_URL */
export const SPLINE_BG_URL = HERO_SPLINE_URL;
export const SPLINE_BG_EMBED_KEY = HERO_SPLINE_EMBED_KEY;

export const HERO_SPLINE_ENABLED = true;

/** Mobile hero — WebGL off; CSS ambient background only. */
export const HERO_SPLINE_MOBILE_ENABLED = false;

export const HERO_BRAND_ACCENT = "#0055FF";

export const SPLINE_RENDER_WIDTH = 1280;
export const SPLINE_RENDER_HEIGHT = 720;
export const SPLINE_RENDER_WIDTH_MOBILE = 960;
export const SPLINE_RENDER_HEIGHT_MOBILE = 540;

/** Mobile main hero robot — tall portrait canvas; Spline reframes to fit full robot + logo */
export const HERO_SPLINE_RENDER_WIDTH_MOBILE = 480;
export const HERO_SPLINE_RENDER_HEIGHT_MOBILE = 1280;
/** contain scale multiplier — zoom out so chest logo is not clipped */
export const HERO_SPLINE_MOBILE_SCALE_INSET = 0.8;
/** Stage nudge up (negative %) to reveal bottom logo text on robot body */
export const HERO_SPLINE_MOBILE_STAGE_OFFSET_Y = -7;

export const HERO_AI_BAR_ENABLED = true;

/** TODO: 히어로 왼쪽 카피 영역 — 잠시 숨김. 보관본: /about → SavedHeroLeftCopySection */
export const HERO_LEFT_COPY_ENABLED = false;

/** Mobile hero — same Vesper Spline iframe as desktop (blue waves). */
export const MOBILE_HERO_USE_VESPER_SPLINE = true;

/**
 * Fallback PNG when Spline is unavailable (e.g. prefers-reduced-motion).
 * Save a Vesper / blue-wave capture as: frontend/public/images/hero-mobile-vesper.png
 */
export const MOBILE_HERO_FALLBACK_IMG = "/images/hero-mobile-vesper.png";

/**
 * @deprecated About-page 3D cards capture — NOT the homepage blue-wave hero.
 * Do not replace this file expecting the main hero to change.
 */
export const MOBILE_HERO_STATIC_IMG_LEGACY = "/images/hero-spline-static.png";

/** Mobile landing — always show copy + static visual (desktop may hide copy for Spline). */
export const HERO_MOBILE_CONTENT_ENABLED = true;

export function isHeroSplineEnabled({
  useMediaBg,
  embedMode,
  previewDevice,
  isMobileViewport = false,
}) {
  if (!HERO_SPLINE_ENABLED) return false;
  if (isMobileViewport && !HERO_SPLINE_MOBILE_ENABLED) return false;
  return !useMediaBg && (!embedMode || Boolean(previewDevice));
}

export const HERO_STATS = [
  { to: 19438, suffix: "+", label: "완료 프로젝트", accent: false, testId: "stat-projects" },
  { to: 384, suffix: "+", label: "AI 자동화 통합", accent: false, testId: "stat-automations" },
  { to: 12902, suffix: "+", label: "월간 처리 요청", accent: false, testId: "stat-requests" },
  { to: 99, suffix: ".9%", label: "서비스 안정성", accent: true, testId: "stat-uptime" },
];

export const MOBILE_HERO_MAX_WIDTH = 600;

export const DEFAULT_HERO_SUBTITLE =
  "매출은 높이고, 운영은 스마트하게.\n\n디지털 기술로 맞춤형 웹 플랫폼, AI 솔루션, 업무 자동화를 연결하여\n고객의 매출 성장과 운영 효율을 극대화하는 비즈니스를 설계합니다.";

export function parseHeroSubtitle(raw) {
  const text = (raw || DEFAULT_HERO_SUBTITLE).trim();
  const parts = text.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return { lead: "", body: "" };
  if (parts.length === 1) return { lead: parts[0], body: "" };
  return { lead: parts[0], body: parts.slice(1).join("\n\n") };
}

export function formatHeroTitle(raw) {
  const fallback = "기획·개발·운영까지\n성장을 설계하는 디지털 파트너";
  const text = (raw || fallback).trim();
  if (text.includes("\n")) return text;
  return fallback;
}

export function parseTitleLines(cmsTitle, highlight) {
  const titleText = formatHeroTitle(cmsTitle);
  const titleParts = titleText.split("\n");
  const titleLine1 = titleParts[0] ?? "";
  const titleLine2 = titleParts.slice(1).join(" ").trim();

  let titleLine2Before = titleLine2;
  let titleLine2Highlight = "";
  let titleLine2After = "";

  if (highlight && titleLine2.includes(highlight)) {
    const idx = titleLine2.indexOf(highlight);
    titleLine2Before = titleLine2.slice(0, idx);
    titleLine2Highlight = highlight;
    titleLine2After = titleLine2.slice(idx + highlight.length);
  }

  return { titleLine1, titleLine2Before, titleLine2Highlight, titleLine2After };
}

export function formatPreviewStat(stat) {
  if (stat.suffix === ".9%") return `${stat.to}${stat.suffix}`;
  return `${stat.to.toLocaleString("ko-KR")}${stat.suffix}`;
}

export function formatStatCountInteger(n) {
  return n.toLocaleString("ko-KR");
}

export function formatStatCountPercent(n) {
  return n.toFixed(1);
}

export function getHeroStatCountUpProps(stat, index) {
  const isStability = stat.suffix === ".9%";
  if (isStability) {
    return {
      to: 99.9,
      from: 88.9,
      decimals: 1,
      duration: 2400,
      suffix: "%",
      formatter: formatStatCountPercent,
      loop: false,
      testId: stat.testId,
    };
  }
  return {
    to: stat.to,
    from: 1,
    decimals: 0,
    duration: 2400 + index * 200,
    suffix: stat.suffix,
    formatter: formatStatCountInteger,
    loop: false,
    testId: stat.testId,
  };
}

export function HeroBlock({ embedMode, as = "div", motionConfig, revealOnScroll = false, className, children, ...rest }) {
  if (embedMode) {
    const Tag = as;
    return (
      <Tag className={className} {...rest}>
        {children}
      </Tag>
    );
  }

  const MotionTag =
    as === "h1" ? motion.h1 : as === "h2" ? motion.h2 : as === "p" ? motion.p : motion.div;
  const motionProps = revealOnScroll
    ? {
        initial: motionConfig.initial,
        whileInView: motionConfig.animate,
        viewport: { once: true, amount: 0.25 },
        transition: motionConfig.transition,
      }
    : motionConfig;

  return (
    <MotionTag className={className} {...motionProps} {...rest}>
      {children}
    </MotionTag>
  );
}
